import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/Layout';
import { useCategories } from './useCategories';
import { categoryService, catalogueVendorsByTypeService, VendorByTypeItem } from '../../services/category.service';
import type { SubCategory, CategoryType } from '../../types/category.types';

/**
 * Match backend `generateKeyFromName` (used when creating catalog keys).
 * Must collapse repeated dashes so "Spa & Facial" → "spa-facial", not "spa--facial".
 */
function generateKeyFromName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Prefer API catalog key; fall back to the same slug rule as the backend. */
function resolveTypeKey(typeObj: CategoryType): string {
  return typeObj.key || generateKeyFromName(typeObj.name);
}

const SortableVendorRow: React.FC<{
  vendor: VendorByTypeItem;
  index: number;
}> = ({ vendor, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: vendor.vendorId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
      >
        <DragIndicatorIcon color="action" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 28 }}>
        {index + 1}.
      </Typography>
      {vendor.profileImageUrl ? (
        <Box
          component="img"
          src={vendor.profileImageUrl}
          alt=""
          sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
        />
      ) : (
        <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: 'action.hover' }} />
      )}
      <Typography variant="body2" fontWeight={500}>
        {vendor.companyName || vendor.vendorId}
      </Typography>
    </Box>
  );
};

const CategoryDetails: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { categories, loading: categoriesLoading } = useCategories();

  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const [selectedType, setSelectedType] = useState<CategoryType | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [vendors, setVendors] = useState<VendorByTypeItem[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);

  const category = useMemo(
    () => (categoryId ? categories.find((c) => c.id === categoryId) : null),
    [categories, categoryId]
  );

  const subCategories = useMemo(() => category?.subCategories ?? [], [category]);
  const typesForSub = useMemo(
    () => (selectedSubCategory ? selectedSubCategory.types ?? [] : []),
    [selectedSubCategory]
  );
  const itemsForType = useMemo(
    () => (selectedType ? selectedType.items ?? [] : []),
    [selectedType]
  );

  const fetchVendors = useCallback(
    async (typeKey: string, item?: string) => {
      setVendorsLoading(true);
      try {
        const res = await catalogueVendorsByTypeService.getVendorsByType(typeKey, item);
        setVendors(res.vendors ?? []);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to load vendors');
        setVendors([]);
      } finally {
        setVendorsLoading(false);
      }
    },
    []
  );

  const handleSelectSubCategory = useCallback((sub: SubCategory) => {
    setSelectedSubCategory(sub);
    setSelectedType(null);
    setSelectedItem(null);
    setVendors([]);
  }, []);

  const handleSelectType = useCallback(
    (typeObj: CategoryType) => {
      setSelectedType(typeObj);
      setSelectedItem(null);
      fetchVendors(resolveTypeKey(typeObj));
    },
    [fetchVendors]
  );

  const handleSelectItem = useCallback(
    (item: string) => {
      setSelectedItem(item);
      if (!selectedType) return;
      fetchVendors(resolveTypeKey(selectedType), item);
    },
    [selectedType, fetchVendors]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = vendors.findIndex((v) => v.vendorId === active.id);
      const newIndex = vendors.findIndex((v) => v.vendorId === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const next = [...vendors];
      const [removed] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, removed);
      setVendors(next);
      if (!selectedType) return;
      const typeKey = resolveTypeKey(selectedType);
      setReorderSaving(true);
      try {
        await catalogueVendorsByTypeService.reorderVendorsByType(
          typeKey,
          next.map((v) => v.vendorId),
          selectedItem ?? undefined
        );
        toast.success('Order saved');
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to save order');
        setVendors(vendors);
      } finally {
        setReorderSaving(false);
      }
    },
    [vendors, selectedType, selectedItem]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const breadcrumbParts = useMemo(() => {
    const parts: { label: string; onClick?: () => void }[] = [];
    if (category) {
      parts.push({ label: category.name, onClick: () => navigate('/categories') });
    }
    if (selectedSubCategory) {
      parts.push({ label: selectedSubCategory.name });
    }
    if (selectedType) {
      parts.push({ label: selectedType.name });
    }
    if (selectedItem) {
      parts.push({ label: selectedItem });
    }
    return parts;
  }, [category, selectedSubCategory, selectedType, selectedItem, navigate]);

  if (categoriesLoading || !categoryId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress />
      </Box>
    );
  }

  if (!category) {
    return (
      <Box p={3}>
        <Typography color="text.secondary">Category not found.</Typography>
        <Link component="button" variant="body2" onClick={() => navigate('/categories')} sx={{ mt: 1, display: 'block' }}>
          Back to Categories
        </Link>
      </Box>
    );
  }

  return (
    <Box
      className="page-container"
      sx={{
        padding: { xs: '24px 16px', sm: '40px 40px', md: '40px 80px' },
        gap: 2,
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PageHeader
        title="Category details"
        showSearch={false}
        primaryButton={false}
      />

      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        {breadcrumbParts.map((part, i) =>
          part.onClick ? (
            <Link
              key={i}
              component="button"
              variant="body2"
              underline="hover"
              color="inherit"
              onClick={part.onClick}
            >
              {part.label}
            </Link>
          ) : (
            <Typography key={i} variant="body2" color="text.primary">
              {part.label}
            </Typography>
          )
        )}
      </Breadcrumbs>

      {!selectedSubCategory && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Select a sub-category
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {subCategories.map((sub) => (
              <Chip
                key={sub.name}
                label={sub.name}
                onClick={() => handleSelectSubCategory(sub)}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {selectedSubCategory && !selectedType && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Select a type
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {typesForSub.map((t) => (
              <Chip
                key={t.name}
                label={t.name}
                onClick={() => handleSelectType(t)}
                sx={{
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '&:hover': { bgcolor: 'secondary.dark' },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {selectedType && (
        <Box display="flex" flexDirection="column" gap={2} flex={1}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Items (optional – select to filter vendors by type + item)
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Chip
                label="All (type only)"
                onClick={() => {
                  setSelectedItem(null);
                  if (selectedType) fetchVendors(resolveTypeKey(selectedType));
                }}
                variant={selectedItem === null ? 'filled' : 'outlined'}
                color="primary"
              />
              {itemsForType.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  onClick={() => handleSelectItem(item)}
                  variant={selectedItem === item ? 'filled' : 'outlined'}
                  color="primary"
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Vendors {reorderSaving && '(saving…)'}
            </Typography>
            {vendorsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : vendors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No vendors for this selection.
              </Typography>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={vendors.map((v) => v.vendorId)}
                  strategy={verticalListSortingStrategy}
                >
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    {vendors.map((vendor, index) => (
                      <SortableVendorRow key={vendor.vendorId} vendor={vendor} index={index} />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CategoryDetails;
